/**
 * ========================================
 * 🎟️ TICKET V4-P0-007: ErrorDisplay Component
 * ========================================
 * Componente React para mostrar errores técnicos con:
 * - correlationId copiable al clipboard
 * - Timestamp del error
 * - Mensaje de error técnico + user-friendly
 * - Botón de retry (opcional)
 * - Diseño consistente con EPL UX/UI Policy
 * 
 * VARIANTES:
 * - inline: Mensaje de error dentro del timeline (default)
 * - banner: Banner superior para errores críticos
 * - modal: Modal blocking para errores que requieren acción
 */

import React, { useState } from 'react';
import { AlertTriangle, XCircle, Copy, Check, RefreshCw, X } from 'lucide-react';
import { copyCorrelationId } from '../../../utils/safeFetchJson';

/**
 * @param {Object} props
 * @param {string} props.error - Mensaje de error técnico
 * @param {string} [props.userMessage] - Mensaje amigable para el usuario
 * @param {string} [props.correlationId] - ID de correlación para soporte
 * @param {number} [props.timestamp] - Timestamp del error (milisegundos)
 * @param {string} [props.variant='inline'] - Variante visual: 'inline', 'banner', 'modal'
 * @param {Function} [props.onRetry] - Callback para reintentar acción
 * @param {Function} [props.onDismiss] - Callback para cerrar error
 * @param {string} [props.className] - Clases CSS adicionales
 */
const ErrorDisplay = ({
  error,
  userMessage,
  correlationId,
  timestamp,
  variant = 'inline',
  onRetry,
  onDismiss,
  className = ''
}) => {
  const [copied, setCopied] = useState(false);

  // Formatear timestamp
  const formattedTime = timestamp 
    ? new Date(timestamp).toLocaleTimeString('es-ES', { 
        hour: '2-digit', 
        minute: '2-digit', 
        second: '2-digit' 
      })
    : null;

  // Copiar correlationId al clipboard
  const handleCopy = async () => {
    if (!correlationId) return;
    
    const success = await copyCorrelationId(correlationId);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Mensaje user-friendly (fallback si no se provee)
  const displayUserMessage = userMessage || 
    'Ocurrió un error inesperado. Por favor, intenta de nuevo o contacta a soporte técnico.';

  // ========== VARIANTE: INLINE (en timeline) ==========
  if (variant === 'inline') {
    return (
      <div className={`bg-red-50 border-l-4 border-red-500 rounded-r-lg p-4 my-2 ${className}`}>
        {/* Header con ícono y título */}
        <div className="flex items-start gap-3">
          <XCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          
          <div className="flex-1 min-w-0">
            {/* Mensaje para el usuario */}
            <p className="text-sm font-medium text-red-900 mb-1">
              {displayUserMessage}
            </p>
            
            {/* Detalles técnicos colapsables */}
            {error && (
              <details className="mt-2">
                <summary className="text-xs text-red-700 cursor-pointer hover:text-red-800 select-none">
                  Detalles técnicos
                </summary>
                <div className="mt-2 p-2 bg-red-100/50 rounded text-xs font-mono text-red-800 break-all">
                  {error}
                </div>
              </details>
            )}
            
            {/* Footer: correlationId + timestamp */}
            <div className="flex flex-wrap items-center gap-3 mt-2 text-xs text-red-700">
              {correlationId && (
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1.5 hover:text-red-900 transition-colors group"
                  title="Copiar ID de error"
                >
                  {copied ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-green-600" />
                      <span className="font-medium text-green-600">Copiado</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 group-hover:scale-110 transition-transform" />
                      <span className="font-mono">{correlationId}</span>
                    </>
                  )}
                </button>
              )}
              
              {formattedTime && (
                <span className="text-red-600">
                  {formattedTime}
                </span>
              )}
            </div>
          </div>

          {/* Botones de acción */}
          <div className="flex gap-2 flex-shrink-0">
            {onRetry && (
              <button
                onClick={onRetry}
                className="p-1.5 hover:bg-red-100 rounded-md transition-colors group"
                title="Reintentar"
              >
                <RefreshCw className="w-4 h-4 text-red-700 group-hover:rotate-180 transition-transform duration-300" />
              </button>
            )}
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-1.5 hover:bg-red-100 rounded-md transition-colors"
                title="Cerrar"
              >
                <X className="w-4 h-4 text-red-700" />
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ========== VARIANTE: BANNER (superior fijo) ==========
  if (variant === 'banner') {
    return (
      <div className={`bg-red-500 text-white shadow-lg ${className}`}>
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 flex-1">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              
              <div className="flex-1 min-w-0">
                <p className="font-medium text-sm">
                  {displayUserMessage}
                </p>
                {correlationId && (
                  <button
                    onClick={handleCopy}
                    className="text-xs text-red-100 hover:text-white flex items-center gap-1.5 mt-1"
                  >
                    {copied ? (
                      <>
                        <Check className="w-3 h-3" />
                        <span>Copiado</span>
                      </>
                    ) : (
                      <>
                        <Copy className="w-3 h-3" />
                        <span className="font-mono">{correlationId}</span>
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>

            <div className="flex items-center gap-2 flex-shrink-0">
              {onRetry && (
                <button
                  onClick={onRetry}
                  className="px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-md text-sm font-medium transition-colors flex items-center gap-2"
                >
                  <RefreshCw className="w-4 h-4" />
                  Reintentar
                </button>
              )}
              
              {onDismiss && (
                <button
                  onClick={onDismiss}
                  className="p-2 hover:bg-white/20 rounded-md transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ========== VARIANTE: MODAL (blocking) ==========
  if (variant === 'modal') {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`bg-white rounded-2xl shadow-2xl max-w-md w-full ${className}`}>
          {/* Header */}
          <div className="flex items-start justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">
                  Error en la operación
                </h3>
                {formattedTime && (
                  <p className="text-sm text-gray-500">{formattedTime}</p>
                )}
              </div>
            </div>
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            )}
          </div>

          {/* Body */}
          <div className="p-6 space-y-4">
            {/* Mensaje usuario */}
            <p className="text-gray-700">
              {displayUserMessage}
            </p>

            {/* Detalles técnicos */}
            {error && (
              <details className="bg-gray-50 rounded-lg p-3">
                <summary className="text-sm font-medium text-gray-700 cursor-pointer select-none">
                  Ver detalles técnicos
                </summary>
                <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                  <code className="text-xs text-red-600 break-all">
                    {error}
                  </code>
                </div>
              </details>
            )}

            {/* CorrelationId */}
            {correlationId && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs font-medium text-blue-900 mb-2">
                  ID de error (para soporte técnico):
                </p>
                <button
                  onClick={handleCopy}
                  className="w-full flex items-center justify-between gap-2 bg-white border border-blue-300 rounded-md px-3 py-2 hover:bg-blue-50 transition-colors group"
                >
                  <code className="text-sm font-mono text-blue-700 flex-1 text-left">
                    {correlationId}
                  </code>
                  {copied ? (
                    <Check className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-blue-600 group-hover:scale-110 transition-transform" />
                  )}
                </button>
              </div>
            )}
          </div>

          {/* Footer con acciones */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            {onRetry && (
              <button
                onClick={onRetry}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg font-medium flex items-center gap-2 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Reintentar
              </button>
            )}
            
            {onDismiss && (
              <button
                onClick={onDismiss}
                className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-700 rounded-lg font-medium transition-colors"
              >
                Cerrar
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default ErrorDisplay;
